import type { Action, AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { combineReducers, configureStore, createListenerMiddleware } from "@reduxjs/toolkit";
import type { ThunkAction } from "redux-thunk";
import type { AppState } from "./app-state";
import rootReducer from "./root-reducer";
import type { SharedRepository } from "@shared-kernel/application/ports/shared/shared-repository";
import type { TeacherRepository } from "@shared-kernel/application/ports/teacher/teacher-repository";
import type { StudentRepository } from "@shared-kernel/application/ports/student/student-repository";
import { addListeners } from "./listeners";
import type { ProviderRepository } from "@shared-kernel/application/ports/provider/provider-repository";
import type { DocumentUploadProvider } from "@shared-kernel/application/ports/shared/document-upload-provider";
import type { EducationalAdvisorRepository } from "@shared-kernel/application/ports/educational-advisor/educational-advisor-repository";
import type { AdminRepository } from "@shared-kernel/application/ports/admin/admin-repository";
import type { PublicRepository } from "@shared-kernel/application/ports/public/public-repository";
import type { SiretSearchRepository } from "@shared-kernel/application/ports/siret-search/siret-search-repository";

export interface Dependencies {
  sharedRepository: SharedRepository;
  teacherRepository: TeacherRepository;
  studentRepository: StudentRepository;
  providerRepository: ProviderRepository;
  documentUploadProvider: DocumentUploadProvider;
  educationalAdvisorRepository: EducationalAdvisorRepository;
  adminRepository: AdminRepository;
  publicRepository: PublicRepository;
  siretSearchRepository: SiretSearchRepository;
}

export const listenerMiddleware = createListenerMiddleware<
  AppState,
  ThunkDispatch<AppState, Partial<Dependencies>, AnyAction>,
  Partial<Dependencies>
>();
addListeners(listenerMiddleware);

import type { DeepPartial } from "./state/shared-types";

export const initReduxStore = (dependencies: Partial<Dependencies>, preloadedState?: DeepPartial<AppState>) => {
  return configureStore({
    preloadedState: preloadedState as AppState,
    reducer: combineReducers(rootReducer),
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: dependencies,
        },
      }).prepend(listenerMiddleware.middleware),
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AppThunk<R> = ThunkAction<R, AppState, any, Action>;
export type ReduxStore = ReturnType<typeof initReduxStore>;
